import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getSubmissionDetails, getUwBRReview, getUwBRSubscribers } from 'application/redux/selectors';
import { useActions } from 'hooks/useActions';
import * as Actions from 'application/redux/actions';
import CarrierSubscription from 'components/Underwriting/components/CarrierSubscription';
import { useErrorDialog } from 'components/ErrorDialog';
import { fetchSubscriptionCalculation } from 'services/uwReview';
import { SubscriberParticipationResource, SubscriberResource } from 'types/underwriting.type';
import { unformatNumberString } from 'helpers/formatters';
import moment from 'moment';
import { PRODUCT_LINE_TYPES } from 'constants/index';

interface Props {
    readOnly: boolean;
}

const DEBOUNCE_MS = 500;
const MY_PRODUCT_LINE_TYPE = PRODUCT_LINE_TYPES.BUILDERS_RISK;

const BuildersRiskSubscription: React.FC<Props> = ({ readOnly }) => {
    const submission = useSelector(getSubmissionDetails);
    const uwBrReview = useSelector(getUwBRReview);
    const subscriptionCarriers = useSelector(getUwBRSubscribers);
    const { uwReviewSaveBRSubscription } = useActions(Actions);
    const { showError } = useErrorDialog();

    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hasFetchedInitial = useRef(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    // Build the ISO effective date from the submission (which stores it as MM/DD/YYYY)
    const effectiveDateIso = React.useMemo(() => {
        if (!submission?.effectiveDate) return '';
        const parsed = moment(submission.effectiveDate, ['MM/DD/YYYY', 'YYYY-MM-DD'], true);
        return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
    }, [submission?.effectiveDate]);

    /**
     * Build a SubscriberParticipationResource list from the current subscribers in Redux.
     * Used to preserve participation state across re-fetches triggered by limit changes.
     */
    const buildParticipationList = (carriers: SubscriberResource[]): SubscriberParticipationResource[] =>
        carriers.map(({ programSubscriberId, isParticipating }) => ({
            programSubscriberId: programSubscriberId,
            isParticipating,
        }));

    /**
     * POST to subscription-calculations. Sends the full SubscriptionCalculationRequest including
     * the optional subscribers participation list so the back end can recalculate values.
     */
    const loadSubscriptionCalculation = React.useCallback(
        (lumenLimit?: number, policyLimit?: number, subscribers?: SubscriberParticipationResource[]) => {
            if (!effectiveDateIso) return;
            fetchSubscriptionCalculation({
                effectiveDate: effectiveDateIso,
                productLineType: MY_PRODUCT_LINE_TYPE,
                subscriptionId: uwBrReview?.subscription?.id ?? undefined,
                lumenLimitOfLiability: lumenLimit || undefined,
                policyLimitOfLiability: policyLimit || undefined,
                subscribers: subscribers?.length ? subscribers : undefined,
            })
                .then((resource) => {
                    if (resource?.subscribers) {
                        // Back end returns updated isParticipating, so no client-side merge needed.
                        const updated: SubscriberResource[] = resource.subscribers.map((s) => ({
                            ...s,
                            isParticipating: s.isParticipating ?? true,
                        }));
                        uwReviewSaveBRSubscription(updated);
                    }
                })
                .catch(() => {
                    setHasError(true);
                    showError({
                        title: 'API Error',
                        message:
                            'An error occurred while loading subscription data. Please try again or contact support.',
                    });
                })
                .finally(() => {
                    setIsLoading(false);
                });
        },
        [effectiveDateIso, uwBrReview?.subscription?.id, uwReviewSaveBRSubscription]
    );

    // Initial fetch: only when creating a new review (no existing id) and we haven't fetched yet
    useEffect(() => {
        if (hasFetchedInitial.current) return;
        if (!effectiveDateIso) return;
        if (subscriptionCarriers && subscriptionCarriers.length > 0) {
            hasFetchedInitial.current = true;
            return;
        }
        if (!uwBrReview?.id) {
            hasFetchedInitial.current = true;
            loadSubscriptionCalculation();
        }
    }, [effectiveDateIso, uwBrReview?.id]);

    // Re-fetch when policyLimitOfLiability or lumenLimitOfLiability change (debounced)
    const policyLimit = unformatNumberString(uwBrReview?.policyLimitOfLiability || 0);
    const lumenLimit = unformatNumberString(uwBrReview?.lumenLimitOfLiability || 0);

    const prevPolicyLimit = useRef(policyLimit);
    const prevLumenLimit = useRef(lumenLimit);

    useEffect(() => {
        const policyChanged = policyLimit !== prevPolicyLimit.current;
        const lumenChanged = lumenLimit !== prevLumenLimit.current;
        prevPolicyLimit.current = policyLimit;
        prevLumenLimit.current = lumenLimit;

        if (!policyChanged && !lumenChanged) return;
        if (!effectiveDateIso) return;

        // Pass current participation state so the back end preserves user choices
        const subscribers = buildParticipationList(subscriptionCarriers || []);

        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            setIsLoading(true);
            loadSubscriptionCalculation(lumenLimit || undefined, policyLimit || undefined, subscribers);
        }, DEBOUNCE_MS);

        return () => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
        };
    }, [policyLimit, lumenLimit]);

    /**
     * When the user toggles a carrier's participation:
     * 1. Immediately update Redux so the checkbox reflects the change instantly.
     * 2. POST to the back end with the updated participation list so it can
     *    recalculate programLimit / programPercentage for the new participating set.
     */
    const handleCarriersChange = (updated: SubscriberResource[]) => {
        const subscribers = buildParticipationList(updated);

        setIsLoading(true);
        loadSubscriptionCalculation(lumenLimit || undefined, policyLimit || undefined, subscribers);
    };

    return hasError ? null : (
        <CarrierSubscription
            carriers={subscriptionCarriers || []}
            readOnly={readOnly}
            onChange={handleCarriersChange}
            isLoading={isLoading}
        />
    );
};

export default React.memo(BuildersRiskSubscription);
